import { useEffect, useState } from "react";
import {
  Camera,
  Clock3,
  ExternalLink,
  ImageOff,
  Instagram,
  Radio,
  RefreshCw,
} from "lucide-react";
import { Seal, Sprig } from "./ornaments";
import { pl } from "./text";

type LiveGalleryItem = {
  id: string;
  caption?: string;
  mediaType: "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
  mediaUrl: string;
  permalink: string;
  timestamp: string;
  thumbnailUrl?: string;
};

type LiveGalleryFeed = {
  hashtag: string;
  status: "live" | "stale" | "disabled" | "not_configured" | "error";
  items: LiveGalleryItem[];
  updatedAt: string | null;
  nextRefreshAt: string | null;
  liveFetchEnabled: boolean;
  pollIntervalSeconds: number;
  message?: string;
};

type FeedState = {
  phase: "loading" | "ready" | "error";
  feed: LiveGalleryFeed | null;
  message: string;
};

type LiveEventsState = "idle" | "connecting" | "connected" | "disabled" | "error";

const initialFeedState: FeedState = {
  phase: "loading",
  feed: null,
  message: "",
};

const fallbackHashtag = "ewelinaimateusz";
const fallbackPollIntervalSeconds = 10;

const dateTimeFormatter = new Intl.DateTimeFormat("pl-PL", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return dateTimeFormatter.format(date);
}

function captionPreview(caption: string | undefined) {
  const cleanCaption = caption?.replace(/\s+/g, " ").trim();
  if (!cleanCaption) {
    return "Zdjęcie oznaczone naszym hashtagiem.";
  }

  return cleanCaption.length > 150
    ? `${cleanCaption.slice(0, 147).trim()}...`
    : cleanCaption;
}

function isLiveGalleryStatus(value: unknown): value is LiveGalleryFeed["status"] {
  return (
    value === "live" ||
    value === "stale" ||
    value === "disabled" ||
    value === "not_configured" ||
    value === "error"
  );
}

function isLiveGalleryItem(value: unknown): value is LiveGalleryItem {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const item = value as Partial<LiveGalleryItem>;
  return (
    typeof item.id === "string" &&
    typeof item.mediaUrl === "string" &&
    typeof item.permalink === "string" &&
    typeof item.timestamp === "string" &&
    (item.mediaType === "IMAGE" ||
      item.mediaType === "VIDEO" ||
      item.mediaType === "CAROUSEL_ALBUM")
  );
}

function parseEventData(event: MessageEvent<string>) {
  try {
    return JSON.parse(event.data) as unknown;
  } catch (_error) {
    return null;
  }
}

function normalizeFeedPayload(payload: Partial<LiveGalleryFeed> | null): LiveGalleryFeed {
  const status = isLiveGalleryStatus(payload?.status) ? payload.status : "live";
  const pollIntervalSeconds =
    typeof payload?.pollIntervalSeconds === "number" &&
    Number.isFinite(payload.pollIntervalSeconds)
      ? payload.pollIntervalSeconds
      : fallbackPollIntervalSeconds;

  return {
    hashtag: typeof payload?.hashtag === "string" ? payload.hashtag : fallbackHashtag,
    status,
    items: Array.isArray(payload?.items) ? payload.items.filter(isLiveGalleryItem) : [],
    updatedAt: typeof payload?.updatedAt === "string" ? payload.updatedAt : null,
    nextRefreshAt:
      typeof payload?.nextRefreshAt === "string" ? payload.nextRefreshAt : null,
    liveFetchEnabled:
      typeof payload?.liveFetchEnabled === "boolean"
        ? payload.liveFetchEnabled
        : status !== "disabled",
    pollIntervalSeconds,
    message: typeof payload?.message === "string" ? payload.message : undefined,
  };
}

function mergeFeedItem(feed: LiveGalleryFeed | null, item: LiveGalleryItem) {
  const base = feed ?? normalizeFeedPayload(null);
  return {
    ...base,
    status: "live",
    items: [item, ...base.items.filter((existing) => existing.id !== item.id)],
    updatedAt: new Date().toISOString(),
  } satisfies LiveGalleryFeed;
}

function statusLabel(
  feed: LiveGalleryFeed | null,
  phase: FeedState["phase"],
  liveUpdatesEnabled: boolean,
  liveEventsState: LiveEventsState,
) {
  if (phase === "loading") {
    return "Ładujemy zdjęcia";
  }
  if (!feed) {
    return "Galeria niedostępna";
  }
  if (feed.status === "not_configured") {
    return "Połączenie w przygotowaniu";
  }
  if (feed.status === "disabled") {
    return "Galeria live czeka na dzień ślubu";
  }
  if (feed.status === "stale") {
    return "Pokazujemy ostatnie zapisane zdjęcia";
  }
  if (feed.status === "error") {
    return "Galeria chwilowo niedostępna";
  }
  if (liveUpdatesEnabled && liveEventsState === "connected") {
    return "Aktualizacje live są włączone";
  }
  if (liveUpdatesEnabled) {
    return "Łączymy aktualizacje live";
  }
  return "Galeria gotowa";
}

function liveUpdatesCopy(
  feed: LiveGalleryFeed | null,
  liveUpdatesEnabled: boolean,
  liveEventsState: LiveEventsState,
) {
  if (feed?.status === "disabled" || feed?.liveFetchEnabled === false) {
    return "Wyłączone do dnia ślubu";
  }
  if (feed?.status === "not_configured") {
    return "Czeka na konfigurację";
  }
  if (liveUpdatesEnabled && liveEventsState === "connected") {
    return `Sprawdzamy co ${feed?.pollIntervalSeconds ?? fallbackPollIntervalSeconds} s`;
  }
  if (liveUpdatesEnabled && liveEventsState === "error") {
    return "Ponawiamy połączenie";
  }
  if (liveUpdatesEnabled) {
    return "Łączymy z serwerem";
  }
  return "Włącz odbiór nowych zdjęć";
}

export function LiveGalleryPage() {
  const [state, setState] = useState<FeedState>(initialFeedState);
  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(false);
  const [liveEventsState, setLiveEventsState] = useState<LiveEventsState>("idle");

  useEffect(() => {
    let disposed = false;
    const activeController = new AbortController();

    async function loadFeed() {
      setState((current) =>
        current.feed ? current : { phase: "loading", feed: null, message: "" },
      );

      try {
        const response = await fetch("/api/instagram/live", {
          headers: { accept: "application/json" },
          signal: activeController.signal,
        });
        const payload = (await response.json().catch(() => null)) as
          | Partial<LiveGalleryFeed>
          | null;

        if (!response.ok) {
          throw new Error(
            typeof payload?.message === "string"
              ? payload.message
              : "Nie udało się pobrać galerii.",
          );
        }

        const feed = normalizeFeedPayload(payload);

        if (!disposed) {
          setState({ phase: "ready", feed, message: "" });
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }

        if (!disposed) {
          setState((current) => ({
            phase: "error",
            feed: current.feed,
            message:
              error instanceof Error
                ? error.message
                : "Nie udało się pobrać galerii.",
          }));
        }
      }
    }

    void loadFeed();

    return () => {
      disposed = true;
      activeController.abort();
    };
  }, []);

  useEffect(() => {
    if (!liveUpdatesEnabled) {
      setLiveEventsState("idle");
      return;
    }

    if (!("EventSource" in window)) {
      setLiveEventsState("error");
      setLiveUpdatesEnabled(false);
      setState((current) => ({
        phase: "error",
        feed: current.feed,
        message: "Ta przeglądarka nie obsługuje aktualizacji live.",
      }));
      return;
    }

    let disposed = false;
    const source = new EventSource("/api/instagram/live/events");
    setLiveEventsState("connecting");

    function closeLiveStream(nextState: LiveEventsState) {
      if (!disposed) {
        setLiveEventsState(nextState);
        setLiveUpdatesEnabled(false);
      }
      source.close();
    }

    function updateFromFeedEvent(event: MessageEvent<string>) {
      const payload = normalizeFeedPayload(
        parseEventData(event) as Partial<LiveGalleryFeed> | null,
      );

      if (disposed) {
        return;
      }

      setState({ phase: "ready", feed: payload, message: "" });

      if (payload.status === "disabled" || payload.status === "not_configured") {
        closeLiveStream("disabled");
      }
      if (payload.status === "error") {
        closeLiveStream("error");
      }
    }

    source.addEventListener("open", () => {
      if (!disposed) {
        setLiveEventsState("connected");
      }
    });
    source.addEventListener("snapshot", updateFromFeedEvent);
    source.addEventListener("sync", updateFromFeedEvent);
    source.addEventListener("state", updateFromFeedEvent);
    source.addEventListener("photo", (event) => {
      const payload = parseEventData(event);
      if (!isLiveGalleryItem(payload) || disposed) {
        return;
      }

      setState((current) => ({
        phase: "ready",
        feed: mergeFeedItem(current.feed, payload),
        message: "",
      }));
    });
    source.onerror = () => {
      if (!disposed) {
        setLiveEventsState("error");
      }
    };

    return () => {
      disposed = true;
      source.close();
    };
  }, [liveUpdatesEnabled]);

  const feed = state.feed;
  const items = feed?.items ?? [];
  const updatedAt = formatDateTime(feed?.updatedAt);
  const nextRefreshAt = formatDateTime(feed?.nextRefreshAt);
  const message = state.message || feed?.message;
  const hashtag = feed?.hashtag ?? fallbackHashtag;
  const liveSwitchDisabled =
    state.phase === "loading" ||
    feed?.status === "disabled" ||
    feed?.status === "not_configured" ||
    feed?.liveFetchEnabled === false;

  return (
    <div className="live-page">
      <header className="live-topbar">
        <a className="brand" href="/" aria-label="Ewelina i Mateusz — strona główna">
          <Seal className="brand-seal" />
          <span className="brand-name">
            Ewelina <em>i</em> Mateusz
          </span>
        </a>
        <a className="live-home-link" href="/">
          Strona główna
        </a>
      </header>

      <main className="live-main">
        <section className="live-hero">
          <div className="live-hero-image" aria-hidden="true" />
          <div className="live-hero-scrim" aria-hidden="true" />
          <div className="live-hero-content">
            <Sprig />
            <p className="eyebrow">Galeria live</p>
            <h1>
              Zdjęcia gości z <span>#{hashtag}</span>
            </h1>
            <p>
              {pl(
                "Dodaj zdjęcie na Instagramie, oznacz je hashtagiem #ewelinaimateusz i wróć tutaj po chwili.",
              )}
            </p>
            <div className="live-status-strip" aria-live="polite">
              <span className="live-status-icon">
                <Instagram aria-hidden="true" size={18} strokeWidth={1.8} />
              </span>
              <span>
                {statusLabel(feed, state.phase, liveUpdatesEnabled, liveEventsState)}
              </span>
            </div>
          </div>
        </section>

        <section className="live-feed-section" aria-labelledby="live-feed-title">
          <div className="live-feed-head">
            <div>
              <p className="eyebrow">Hashtag</p>
              <h2 id="live-feed-title">#{hashtag}</h2>
            </div>
            <div className="live-feed-actions">
              <label
                className={`live-switch${liveUpdatesEnabled ? " is-on" : ""}`}
              >
                <input
                  checked={liveUpdatesEnabled}
                  disabled={liveSwitchDisabled}
                  type="checkbox"
                  onChange={(event) =>
                    setLiveUpdatesEnabled(event.currentTarget.checked)
                  }
                />
                <span className="live-switch-track" aria-hidden="true">
                  <span />
                </span>
                <span className="live-switch-copy">
                  <strong>
                    <Radio aria-hidden="true" size={15} />
                    Aktualizacje live
                  </strong>
                  <span>
                    {liveUpdatesCopy(feed, liveUpdatesEnabled, liveEventsState)}
                  </span>
                </span>
              </label>
              <div className="live-meta" aria-label="Stan galerii">
                {updatedAt ? (
                  <span>
                    <Clock3 aria-hidden="true" size={15} />
                    {updatedAt}
                  </span>
                ) : null}
                {nextRefreshAt ? (
                  <span>
                    <RefreshCw aria-hidden="true" size={15} />
                    {nextRefreshAt}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {message ? <p className="live-message">{pl(message)}</p> : null}

          {state.phase === "loading" ? (
            <div className="live-grid" aria-label="Ładowanie galerii">
              {Array.from({ length: 8 }, (_, index) => (
                <div className="live-skeleton" key={index} />
              ))}
            </div>
          ) : items.length > 0 ? (
            <div className="live-grid">
              {items.map((item) => {
                const caption = captionPreview(item.caption);
                return (
                  <article className="live-tile" key={item.id}>
                    <a href={item.permalink} target="_blank" rel="noreferrer">
                      {item.mediaType === "VIDEO" ? (
                        <video
                          src={item.mediaUrl}
                          aria-label={caption}
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img src={item.mediaUrl} alt={caption} loading="lazy" />
                      )}
                      <span className="live-tile-link">
                        <ExternalLink aria-hidden="true" size={16} />
                      </span>
                    </a>
                    <div className="live-tile-caption">
                      <p>{pl(caption)}</p>
                      <span>{formatDateTime(item.timestamp)}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="live-empty">
              <span>
                {feed?.status === "not_configured" || feed?.status === "disabled" ? (
                  <ImageOff aria-hidden="true" size={30} strokeWidth={1.6} />
                ) : (
                  <Camera aria-hidden="true" size={30} strokeWidth={1.6} />
                )}
              </span>
              <h2>
                {feed?.status === "disabled"
                  ? "Galeria live jest wyłączona"
                  : feed?.status === "not_configured"
                  ? "Galeria czeka na połączenie z Instagramem"
                  : "Jeszcze czekamy na pierwsze zdjęcia"}
              </h2>
              <p>
                {pl(
                  feed?.status === "disabled"
                    ? "Zostawiamy pobieranie zdjęć wyłączone do dnia ślubu."
                    : feed?.status === "not_configured"
                    ? "Po dodaniu danych Meta ta strona zacznie pobierać publiczne zdjęcia z hashtagiem."
                    : "Gdy na Instagramie pojawią się publiczne posty z naszym hashtagiem, trafią tutaj automatycznie.",
                )}
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
